import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Search, Users } from 'lucide-react';
import CustomInput from '../../../../components/forms/CustomInput';
import EmptyStateTable from '../../../../components/feedback/EmptyStateTable';
import FeatureLocked from '../../../../components/gating/FeatureLocked';
import { teamGhostRows, teamColumns } from '../../../../data/roleManagement/roleManagementData';
import RoleMemberRow from './sections/RoleMemberRow';
import RoleMemberMobileCard from './sections/RoleMemberMobileCard';

const RoleMemberList = ({
  members,
  filteredMembers,
  loading,
  searchQuery,
  setSearchQuery,
  currentUserRole,
  isRolesLocked,
  onDelete
}) => {
  const navigate = useNavigate();
  const showEmptyState = isRolesLocked || (members.length === 0 && !searchQuery);

  return (
    <div className="relative">
      <div className="bg-white rounded-2xl sm:rounded-3xl border border-slate-100 shadow-sm overflow-hidden animate-tab-switch min-h-[480px]">
        {/* Header Tabs & Search Bar */}
        <div className="px-4 sm:px-6 pt-4 sm:pt-5 pb-3 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 sm:gap-4 bg-white">
          <div className="flex items-center gap-2 sm:gap-4">
            <span className="text-xs sm:text-sm font-bold text-[#067978] border-b-2 border-[#067978] pb-2 flex items-center gap-2 whitespace-nowrap">
              All Team Members
              <span className="text-[10px] sm:text-[11px] px-2 py-0.5 rounded-full font-bold bg-[#067978]/10 text-[#067978]">
                {filteredMembers.length}
              </span>
            </span>
          </div>

          {/* Search Bar */}
          <div className="w-full sm:w-72">
            <CustomInput
              type="text"
              placeholder="Search team members..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              icon={Search}
              inputClassName="!h-[36px] !bg-slate-50 text-xs"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-9 h-9 border-[3px] border-slate-200 border-t-[#067978] rounded-full animate-spin"></div>
            <p className="text-xs font-semibold text-slate-400 mt-3 animate-pulse">Loading team members...</p>
          </div>
        ) : showEmptyState ? (
          <EmptyStateTable
            showOverlayCard={!isRolesLocked}
            title="No team members yet"
            description="Invite your operations and booking team members. Assign specific permissions and manage their access credentials in one place."
            primaryAction={{
              label: "Add team member",
              onClick: () => navigate('/role-management/add'),
              icon: <Plus size={16} className="shrink-0" />
            }}
            columns={teamColumns}
            rows={teamGhostRows}
            renderRow={(row, index) => (
              <div
                className={`hidden md:grid grid-cols-12 gap-4 items-center px-6 py-3.5 bg-white border-b border-slate-100 ${
                  row.isSample ? 'hover:bg-slate-50/50' : `${row.opacity || 'opacity-30'} select-none pointer-events-none`
                }`}
              >
                <div className="col-span-1 text-sm text-slate-400 font-normal">{index + 1}</div>
                <div className="col-span-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-teal-50 text-[#067978] flex items-center justify-center font-normal text-xs flex-shrink-0">
                      {row.name.charAt(0)}
                    </div>
                    <div className="min-w-0">
                      <div className="text-slate-800 text-sm font-normal truncate">{row.name}</div>
                      <div className="text-[11px] text-slate-400 font-normal mt-0.5 truncate">{row.email}</div>
                    </div>
                  </div>
                </div>
                <div className="col-span-2">
                  <span className="px-2.5 py-0.5 text-[11px] font-normal rounded-full uppercase tracking-wider inline-block bg-teal-50 text-[#067978] border border-teal-100/60">
                    {row.role}
                  </span>
                </div>
                <div className="col-span-3 text-sm font-normal text-slate-600 font-mono">{row.phone}</div>
                <div className="col-span-1 flex justify-center">
                  <span className="inline-flex items-center gap-1.5 text-xs font-normal text-emerald-600">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                    Active
                  </span>
                </div>
                <div className="col-span-1 flex justify-end">
                  <div className="p-1 rounded-lg text-slate-400 border border-slate-200">
                    <Trash2 size={14} />
                  </div>
                </div>
              </div>
            )}
            renderMobileRow={(row) => (
              <div className={`bg-white border rounded-2xl p-3.5 ${row.isSample ? 'border-slate-200/90 shadow-xs' : 'border-slate-100 opacity-40 select-none pointer-events-none'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-slate-800 text-sm font-normal">{row.name}</h4>
                    <p className="text-[11px] text-slate-400 font-normal">{row.email}</p>
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-normal bg-teal-50 text-[#067978]">
                    {row.role}
                  </span>
                </div>
              </div>
            )}
          />
        ) : filteredMembers.length === 0 ? (
          <div className="text-center py-16 bg-slate-50/50 px-4">
            <Users size={48} className="mx-auto text-slate-300 mb-3" />
            <h3 className="text-base font-normal text-slate-700">No Team Members Found</h3>
            <p className="text-xs text-slate-400 mt-1 mb-4 font-normal">No team members match your search criteria.</p>
            <button onClick={() => setSearchQuery('')} className="text-[#067978] font-normal text-xs hover:underline uppercase tracking-wider cursor-pointer">Clear Search</button>
          </div>
        ) : (
          <div>
            {/* Mobile View (< md) */}
            <div className="md:hidden divide-y divide-slate-100">
              {filteredMembers.map((member) => (
                <RoleMemberMobileCard
                  key={member._id}
                  member={member}
                  currentUserRole={currentUserRole}
                  onDelete={onDelete}
                />
              ))}
            </div>

            {/* Desktop Table View (>= md) */}
            <div className="hidden md:block overflow-x-auto w-full">
              <div className="min-w-full">
                {/* Header */}
                <div className="flex bg-white border-b border-slate-100 text-xs font-bold text-slate-400 items-center">
                  <div className="w-16 sm:w-20 px-6 py-3.5 whitespace-nowrap text-slate-400">Sr no.</div>
                  <div className="flex-1 min-w-[200px] px-6 py-3.5">Team Member</div>
                  <div className="w-32 sm:w-36 px-6 py-3.5">Role</div>
                  <div className="w-36 sm:w-44 px-6 py-3.5">Phone Number</div>
                  <div className="w-28 sm:w-32 px-6 py-3.5 text-center">Status</div>
                  <div className="w-28 sm:w-32 px-4 py-3.5 text-center">Actions</div>
                </div>

                {/* Rows */}
                <div className="divide-y divide-slate-100">
                  {filteredMembers.map((member, index) => (
                    <RoleMemberRow
                      key={member._id}
                      member={member}
                      index={index}
                      currentUserRole={currentUserRole}
                      onDelete={onDelete}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {isRolesLocked && (
        <FeatureLocked featureKey="role-management" overlay={true} />
      )}
    </div>
  );
};

export default RoleMemberList;
